import { useEffect, useRef, useState } from "react";
import { Box, Button } from "@mui/material";
import strings from "global/constants/StringConstants";
import { maskedContent } from "helpers/maskedContent";
import styles from "../ContractCompare.styles";
import { compareColors } from "../ContractCompare.tokens";
import { ArrowRightGlyph, CompareGlyph } from "./ContractCompareIcons";

interface CompareProgressProps {
  oldName: string;
  newName: string;
  onCancel: () => void;
}

/** The three steps are timer-driven: the service reports no real progress, so they only keep the screen alive. */
const STEP_STARTS_SECONDS = [0, 6, 14];
const STILL_WORKING_SECONDS = 20;

/**
 * The comparing view. The active step keeps an indeterminate bar; the elapsed seconds only move the three steps on and,
 * after about 20 seconds, add "Still working…" so it never looks stuck. The page shows no time and no promise of how
 * long it takes. The panel is a polite status region: a screen reader hears the step names and the "still working"
 * line, not a tick every second.
 */
const CompareProgress = ({ oldName, newName, onCancel }: CompareProgressProps) => {
  const page = strings.contractCompare.page;
  const [elapsed, setElapsed] = useState(0);
  const startedAt = useRef(Date.now());

  useEffect(() => {
    const timer = window.setInterval(() => {
      setElapsed(Math.floor((Date.now() - startedAt.current) / 1000));
    }, 1000);
    return () => window.clearInterval(timer);
  }, []);

  const active = STEP_STARTS_SECONDS.reduce((index, start, i) => (elapsed >= start ? i : index), 0);
  const stillWorking = elapsed >= STILL_WORKING_SECONDS;

  return (
    <Box sx={styles.panelWrap}>
      <Box component="section" sx={styles.progressPanel} aria-labelledby="compare-progress-title">
        <Box sx={styles.progressHead}>
          <Box sx={styles.progressIcon} aria-hidden="true">
            <CompareGlyph sx={{ width: 26, height: 26 }} />
          </Box>
          <Box sx={{ minWidth: 0 }}>
            <Box component="h2" id="compare-progress-title" sx={styles.panelTitle(compareColors.ink, "left")}>
              {page.progressTitle}
            </Box>
            <Box sx={styles.progressNames} {...maskedContent}>
              <span dir="auto">{oldName}</span>
              <ArrowRightGlyph sx={{ width: 16, height: 16 }} />
              <span dir="auto">{newName}</span>
            </Box>
          </Box>
        </Box>

        <Box role="status" aria-live="polite" {...maskedContent}>
          <Box component="p" sx={styles.srOnly}>
            {page.progressStarted}
          </Box>
          <Box component="ol" sx={styles.steps2}>
            {page.progressSteps.map((name, index) => {
              const isActive = index === active;
              const done = index < active;
              return (
                <Box component="li" key={name} sx={styles.step(isActive)} aria-current={isActive ? "step" : undefined}>
                  {isActive ? (
                    <Box sx={styles.spinner} aria-hidden="true" />
                  ) : (
                    <Box sx={styles.stepMark} aria-hidden="true">
                      {index + 1}
                    </Box>
                  )}
                  <Box sx={styles.stepBody}>
                    <Box sx={styles.stepLine}>
                      <Box component="span" sx={styles.stepName(isActive || done)}>
                        {name}
                      </Box>
                      {isActive && (
                        <Box component="span" sx={styles.stepState}>
                          {page.progressActive}
                        </Box>
                      )}
                    </Box>
                    {isActive && (
                      <Box sx={styles.track} aria-hidden="true">
                        <Box sx={styles.trackFill} />
                      </Box>
                    )}
                  </Box>
                </Box>
              );
            })}
          </Box>
          {stillWorking && (
            <Box component="p" sx={styles.stillNote}>
              {page.progressStill}
            </Box>
          )}
        </Box>

        <Box sx={styles.progressFoot}>
          <Button variant="outlined" onClick={onCancel} sx={styles.cancelButton}>
            {page.cancel}
          </Button>
        </Box>
      </Box>
    </Box>
  );
};

export default CompareProgress;
